import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { clone } from 'remeda';
import CustomTableViews from 'shared/components/CustomTable/CustomTableViews';
import { validatePairedAmountFilters } from 'shared/components/CustomTable/filters/pairedAmountFilters';
import ExportInvoices from 'shared/components/ExportInvoices';
import { Button } from 'shared/io';
import { useCanUseManualActions } from 'shared/utils/connector';
import { queryParamParser } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { Invoice } from 'types/store/invoice-state';

import BatchActions from './BatchActions';
import styleIdentifiers from './InvoicesList.module.scss';
import { type GetInvoicesViewParams, useGetInvoicesView } from './useGetInvoicesView';

const styles = classNames.bind(styleIdentifiers);

export type ListingProps = {
  invoicesData: Object;
  loadInvoices: Function;
};

// Builds the view-data query params from the URL: view id from the hash, filters
// from the search (parsed the same way the legacy getView saga did).
const paramsFromLocation = (hash: string, search: string): GetInvoicesViewParams => ({
  view_id: hash.slice(1),
  ...queryParamParser(qs.parse(search, { arrayFormat: 'comma' })),
});

export default function InvoicesList() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const company = useCompanyConfiguration();

  const [params, setParams] = useState<GetInvoicesViewParams>(() =>
    paramsFromLocation(location.hash, location.search),
  );
  const { invoices, isInvoicesInitialLoading, refetchInvoices } = useGetInvoicesView(params);

  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [areAllItemsAcrossPagesSelected, setAreAllItemsAcrossPagesSelected] = useState(false);
  const hasManualActionsActive = useCanUseManualActions();

  // Re-sync the query params whenever the URL changes (the orchestrator writes
  // filters/sort/pagination into the URL via history.push).
  useEffect(() => {
    setParams(paramsFromLocation(location.hash, location.search));
  }, [location]);

  const handleFilters = (filters: any) =>
    validatePairedAmountFilters(['total_tvac', 'remaining_balance'])(clone(filters));

  const exportInvoices = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.EXPORT),
      children: <ExportInvoices resourceType="invoice" />,
    });
  };

  const showInvoice = (item: any, e: React.MouseEvent<HTMLTableRowElement>) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      const newTab = window.open(`/invoices/${item.id}`, '_blank');
      if (newTab) newTab.focus();
      return;
    }
    history.push(`/invoices/${item.id}`);
  };

  const openBatchActionsModal = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.FORM.ACTIONS),
      children: (
        <BatchActions
          selectedInvoices={selectedInvoices}
          isSelectedAll={areAllItemsAcrossPagesSelected}
          reload={refetchInvoices}
        />
      ),
    });
  };

  const newInvoice = () => {
    invoiceActions.detailReset();
    history.push('/invoices/new');
  };

  const renderButton = () => {
    if (selectedInvoices.length > 0) {
      return <Button noMargin label={t(i18nKeys.FORM.ACTIONS)} onClick={openBatchActionsModal} />;
    }

    return (
      <>
        {company.unvalidated_invoices_count > 0 && (
          <Button noMargin onClick={() => history.push('/invoices/to-confirm')}>
            {t(i18nKeys.IMPORT_TO_CONFIRM)}
            <div className={styles('text-circle', 'absolute', 'border', 'blue')}>
              {company.unvalidated_invoices_count}
            </div>
          </Button>
        )}
        <Button noMargin label={t(i18nKeys.EXPORT)} onClick={exportInvoices} />
        {hasManualActionsActive && (
          <Button noMargin label={t(i18nKeys.NEW_INVOICE)} onClick={newInvoice} />
        )}
      </>
    );
  };

  return (
    <CustomTableViews
      title={t(i18nKeys.INVOICING.INVOICES_BOOK)}
      callbackAction={refetchInvoices}
      onClickRow={showInvoice}
      actions={renderButton()}
      isLoading={isInvoicesInitialLoading}
      pagination={invoices?.metadata?.pagination}
      handleFilters={handleFilters}
      items={invoices?.data ?? []}
      onChangeSelectionCallback={values => setSelectedInvoices(values as Array<Invoice>)}
      onChangeSelectItemsAcrossAllPagesCallback={setAreAllItemsAcrossPagesSelected}
      tableName="invoices"
    />
  );
}
